import React, { useState } from 'react';
import { Channel, channelKind, channelMark, channelName, isPersonalProfile } from './reach';
import { charsThatFit, networkKey, postLength, postLimit } from './postLength';
import {
  clock,
  isShort,
  isVertical,
  takes,
  videoCheck,
  videoLimitWords,
  type NetworkRule,
  type PostKind,
} from './composerRules';
import type { ComposerVideo } from './VideoField';

/**
 * Write a post → PREVIEW (Prototype v14; Composer Post Types and Video spec): the
 * post as it will look on each channel picked, one tab per channel. The part past
 * the limit shows in red; LinkedIn folds after about 210 characters ("…see
 * more"). A video plays in the frame the network shows it in, fitted inside, never
 * cropped. A channel that can't take the post says so instead of showing it.
 */

const LINKEDIN_FOLD = 210;
const MEDIA_FIRST = new Set(['instagram', 'tiktok', 'youtube']);

/** The note under a preview; the video's limits come from the one table. */
function noteFor(key: string, rule: NetworkRule | null) {
  const video = videoLimitWords(rule?.video);
  const say = (lead: string) => (video ? `${lead} Video ${video}.` : lead);
  switch (key) {
    case 'x':
      return say('X · 280 characters; a link counts as 23, emoji and “…” as 2.');
    case 'threads':
      return say('Threads · 500 characters; an emoji counts as about 4.');
    case 'linkedin':
      return say('LinkedIn · 3,000 characters; the feed shows about 210.');
    case 'facebook':
      return say('Facebook Page · long posts fold after a few lines.');
    case 'instagram':
      return `Instagram · needs a photo or video. Videos post as Reels, ${video}. Links in captions aren’t clickable.`;
    case 'bluesky':
      return say('Bluesky · 300 characters.');
    case 'youtube':
      return 'YouTube · uploads your video. Title up to 100 characters; your text becomes the description. Vertical videos under 3 minutes post as Shorts.';
    case 'tiktok':
      return `TikTok · video only. Caption up to 2,200 characters; video ${video}.`;
    default:
      return '';
  }
}

const initials = (name: string) =>
  name
    .replace(/^@/, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('') || '?';

function linkCard(link: string) {
  try {
    const u = new URL(/^https?:/i.test(link) ? link : `https://${link}`);
    const last = u.pathname
      .replace(/^\/|\/$/g, '')
      .split('/')
      .pop();
    // A home page link is titled by its domain as written ("tinycms.ai", never "Www.tinycms.ai").
    if (!last) return { domain: u.host, title: u.host.replace(/^www\./i, '') };
    const title = last.replace(/[-_]+/g, ' ');
    return { domain: u.host, title: title.charAt(0).toUpperCase() + title.slice(1) };
  } catch {
    return { domain: link, title: link };
  }
}

const bare = (link: string) => link.replace(/^https?:\/\//i, '');

function VideoFrame({ video, net }: { video: ComposerVideo | null; net: string }) {
  if (!video) {
    return (
      <div className="wp-pv-frame" style={{ aspectRatio: '16 / 9' }}>
        <div className="wp-pv-img-empty wp-pv-frame-empty">your video</div>
      </div>
    );
  }
  const vertical = isVertical(video);
  const short = isShort(net, video);
  const tall = net === 'instagram' || net === 'tiktok' || short;
  const wide = net === 'youtube' && !tall;
  const own = video.width && video.height ? `${video.width} / ${video.height}` : '16 / 9';
  const tag = net === 'instagram' ? 'Reel' : short ? 'Short' : '';
  const busy =
    video.status === 'uploading'
      ? `Uploading ${video.pct}%`
      : video.status === 'processing'
        ? 'Preparing…'
        : '';
  return (
    <div
      className="wp-pv-frame"
      data-narrow={tall || vertical ? 'true' : undefined}
      style={{ aspectRatio: tall ? '9 / 16' : wide ? '16 / 9' : own }}
    >
      {video.src ? (
        <video
          key={video.src}
          src={video.src}
          poster={video.posterUrl || undefined}
          controls
          muted
          playsInline
          preload="metadata"
        />
      ) : video.posterUrl ? (
        <img src={video.posterUrl} alt="" />
      ) : null}
      {tag ? <span className="wp-pv-tag">{tag}</span> : null}
      {busy ? <span className="wp-pv-busy">{busy}</span> : null}
      {!video.src && video.durationSeconds != null ? (
        <span className="wp-vid-dur">{clock(video.durationSeconds)}</span>
      ) : null}
    </div>
  );
}

export function PostPreview({
  channels,
  kind: givenKind,
  textFor,
  link,
  linkTitle,
  imageUrl,
  video: givenVideo = null,
  youtubeTitle = '',
  rulesFor = () => null,
  campaign,
}: {
  channels: Channel[];
  /** Left out (a saved version of a post): by what it carries. */
  kind?: PostKind | '';
  /** The text that goes to this network (its own version, else the main text). */
  textFor: (network: string) => string;
  link: string;
  /** What a picked page or article is called, for the card. */
  linkTitle?: string | null;
  imageUrl: string | null;
  video?: ComposerVideo | null;
  youtubeTitle?: string;
  rulesFor?: (network: string) => NetworkRule | null;
  /** The account's own tracking campaign name, which a link's length includes. */
  campaign?: string | null;
}) {
  const [tabId, setTabId] = useState<string | null>(null);
  const tab = channels.find((c) => c.id === tabId) || channels[0] || null;

  if (!tab) {
    return (
      <div className="wp-pv">
        <div className="wp-pv-head">
          <span className="wp-pv-label">PREVIEW</span>
        </div>
        <div className="wp-pv-empty">Pick a channel to see how the post looks there.</div>
      </div>
    );
  }

  const savedVideo = givenKind === undefined && /\.mp4(\?|$)/i.test(imageUrl || '');
  const kind =
    givenKind ?? (savedVideo ? 'video' : imageUrl ? 'photo' : link.trim() ? 'link' : 'text');
  const isVideo = kind === 'video';
  const video: ComposerVideo | null = savedVideo
    ? {
        key: imageUrl as string,
        assetId: null,
        name: '',
        status: 'ready',
        pct: 100,
        durationSeconds: null,
        width: null,
        height: null,
        sizeBytes: null,
        src: imageUrl,
        url: imageUrl,
        posterUrl: null,
        info: null,
      }
    : givenVideo;
  const carriesMedia = isVideo || kind === 'photo';
  const l = kind === 'text' ? '' : link.trim();
  const context = { media: carriesMedia, campaign };
  // Why a channel shows a box instead of the post, if it does.
  const stopped = (k: string) => {
    const name = channelName(k);
    if (!takes(rulesFor(k), kind)) {
      if (k === 'youtube' || k === 'tiktok') {
        return `${name} only takes video. Nothing goes here as this post is.`;
      }
      return k === 'instagram'
        ? 'Instagram needs a photo or video. Nothing goes here as this post is.'
        : `${name} can’t take this. Nothing goes here as this post is.`;
    }
    if (isVideo && video?.status === 'rejected') return 'Pick a video your plan has minutes for.';
    if (isVideo && video && videoCheck(rulesFor(k), video).state === 'fail') {
      return `This video doesn’t fit ${name}. Leave it out, or pick another video.`;
    }
    return '';
  };

  const key = networkKey(tab.platform);
  const box = stopped(key);
  const text = textFor(key);
  const room = charsThatFit(key, text, l, context);
  const chars = [...text];
  let ok = text;
  let over = '';
  let fold = false;
  if (key === 'linkedin' && chars.length > LINKEDIN_FOLD) {
    ok = chars.slice(0, LINKEDIN_FOLD).join('');
    fold = true;
  } else if (chars.length > room) {
    ok = chars.slice(0, room).join('');
    over = chars.slice(room).join('');
  }
  const personal = isPersonalProfile(tab.platform, tab.profileId);
  const accountKind = channelKind(tab.platform, tab.profileId);
  const who = tab.profileName || tab.profileUsername || channelName(tab.platform);
  const handle = personal
    ? 'Personal profile · posts as you'
    : `${tab.profileUsername ? `@${tab.profileUsername.replace(/^@/, '')}` : who} · ${accountKind}`;
  const inComment = key === 'facebook' && carriesMedia && Boolean(l);
  const tail = l && carriesMedia && !inComment ? bare(l) : '';
  const hasWords = Boolean(text.trim()) || Boolean(tail);
  const mediaFirst = carriesMedia && MEDIA_FIRST.has(key);
  const card = kind === 'link' ? linkCard(l) : null;

  const words = hasWords ? (
    <div className="wp-pv-text" data-below={mediaFirst ? 'true' : undefined}>
      {ok}
      {over ? <span className="wp-pv-over">{over}</span> : null}
      {fold ? <span className="ink-muted">…see more</span> : null}
      {tail ? (
        <span className={key === 'instagram' || key === 'tiktok' ? 'ink-muted' : 'wp-pv-link-tail'}>
          {text.trim() ? ' ' : ''}
          {tail}
        </span>
      ) : null}
    </div>
  ) : (
    <div className="wp-pv-text ink-muted">
      {mediaFirst
        ? key === 'youtube'
          ? 'Description shows here.'
          : 'Caption shows here.'
        : 'Your post shows here as you type.'}
    </div>
  );

  return (
    <div className="wp-pv">
      <div className="wp-pv-head">
        <span className="wp-pv-label">PREVIEW</span>
        <span className="ink-seg wp-pv-tabs" role="tablist" aria-label="Preview channel">
          {channels.map((c) => {
            const k = networkKey(c.platform);
            const lim = postLimit(k);
            const warn =
              Boolean(stopped(k)) || (lim != null && postLength(k, textFor(k), l, context) > lim);
            return (
              <button
                key={c.id}
                type="button"
                role="tab"
                aria-selected={c.id === tab.id}
                data-active={c.id === tab.id}
                onClick={() => setTabId(c.id)}
              >
                {channelName(c.platform)}
                {isPersonalProfile(c.platform, c.profileId) ? ' · you' : ''}
                {warn ? <span className="wp-pv-warn" aria-label="needs attention" /> : null}
              </button>
            );
          })}
        </span>
      </div>
      <div className="wp-pv-card">
        <div className="wp-pv-who">
          <span className="wp-pv-avatar" data-shape={accountKind === 'Page' ? 'square' : 'round'}>
            {initials(who)}
          </span>
          <div className="wp-pv-names">
            <div>{who}</div>
            <div className="ink-muted">{handle}</div>
          </div>
          <span className="rc-mark rc-mark-sm">{channelMark(tab.platform)}</span>
        </div>
        {box ? (
          <div className="wp-pv-stopped">{box}</div>
        ) : (
          <>
            {!mediaFirst ? words : null}
            {kind === 'photo' ? (
              imageUrl ? (
                <img
                  className="wp-pv-img"
                  src={imageUrl}
                  alt=""
                  style={{ aspectRatio: key === 'instagram' ? '4 / 5' : '16 / 10' }}
                />
              ) : (
                <div
                  className="wp-pv-img wp-pv-img-empty"
                  style={{ aspectRatio: key === 'instagram' ? '4 / 5' : '16 / 10' }}
                >
                  your photo
                </div>
              )
            ) : null}
            {isVideo ? <VideoFrame video={video} net={key} /> : null}
            {isVideo && key === 'youtube' ? (
              <div className={youtubeTitle.trim() ? 'wp-pv-yt-title' : 'wp-pv-yt-title ink-muted'}>
                {youtubeTitle.trim() || 'Add a title'}
              </div>
            ) : null}
            {mediaFirst ? words : null}
            {card ? (
              <div className="wp-pv-link">
                <div className="wp-pv-link-image">image from the page</div>
                <div className="ink-muted">{l ? card.domain : 'your link'}</div>
                <div>{l ? linkTitle || card.title : 'Add a link'}</div>
              </div>
            ) : null}
            {inComment ? (
              <div className="wp-pv-comment">
                <span className="wp-pv-label">FIRST COMMENT</span>
                <div className="wp-pv-comment-row">
                  <span className="wp-pv-avatar" data-shape="square">
                    {initials(who)}
                  </span>
                  <div className="wp-pv-comment-body">
                    <div>{who}</div>
                    <div className="wp-pv-link-tail">{bare(l)}</div>
                  </div>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
      {noteFor(key, rulesFor(key)) ? (
        <div className="wp-pv-note">{noteFor(key, rulesFor(key))}</div>
      ) : null}
    </div>
  );
}
